import { useState, useEffect } from "react";
import { Box, Typography, CircularProgress, IconButton } from "@mui/material";
import { Download as DownloadIcon } from "@mui/icons-material";
import { useAppSelector, useAppDispatch } from "../../../../../redux/store";
import { useSnackbar } from "notistack";
import { downloadDropboxFile } from "../../../../../redux/slices/dropboxFoldersSlice";

export const HTMLViewer = ({ htmlContent, htmlData, selectedFile }: any) => {
  const [processedHtml, setProcessedHtml] = useState("");

  const { downloadLoading } = useAppSelector((state) => state.dropboxFolders);
  const dispatch = useAppDispatch();
  const { enqueueSnackbar } = useSnackbar();

  const currentDownloadLoading = selectedFile?.path
    ? downloadLoading[selectedFile.path]
    : false;

  useEffect(() => {
    if (htmlContent) {
      setProcessedHtml(htmlContent);
    } else if (htmlData) {
      // Handle raw data (base64 string or direct HTML string)
      if (typeof htmlData === "string") {
        try {
          // Try to decode as base64 first
          const decoded = atob(htmlData);
          setProcessedHtml(decoded);
        } catch {
          // If not base64, assume it's already HTML
          setProcessedHtml(htmlData);
        }
      } else {
        setProcessedHtml(String(htmlData));
      }
    }
  }, [htmlContent, htmlData]);

  const handleDownload = async () => {
    // For HTML preview files, use download action to download original file
    if (selectedFile?.path) {
      try {
        const result = await dispatch(
          downloadDropboxFile({
            filePath: selectedFile.path,
          })
        ).unwrap();

        // The temporary link is in the 'link' property of the result
        const downloadUrl = result?.link;
        if (downloadUrl) {
          // Use fetch to download the file as blob to ensure proper handling
          const response = await fetch(downloadUrl);
          const blob = await response.blob();

          const url = URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = url;
          link.download = selectedFile.name;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(url);
        }
      } catch (error) {
        console.error("Failed to get temporary link for download:", error);
        enqueueSnackbar("Failed to download file", { variant: "error" });
      }
    }
  };

  return (
    <Box
      sx={{
        width: "100%",
        display: "flex",
        flexDirection: "column",
        height: "100%",
      }}
    >
      {/* HTML Controls */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          p: 1,
          borderBottom: 1,
          borderColor: "divider",
        }}
      >
        <Typography variant="body2">HTML Preview</Typography>
        <Box sx={{ flexGrow: 1 }} />
        <IconButton
          onClick={handleDownload}
          size="small"
          title="Download HTML"
          disabled={currentDownloadLoading}
        >
          {currentDownloadLoading ? (
            <CircularProgress size={24} />
          ) : (
            <DownloadIcon />
          )}
        </IconButton>
      </Box>

      {/* HTML Content */}
      <Box
        sx={{
          flex: 1,
          overflow: "auto",
          p: 1,
        }}
      >
        <iframe
          srcDoc={processedHtml}
          style={{
            width: "100%",
            height: "100%",
            border: "none",
            borderRadius: 0,
          }}
          title="HTML Preview"
        />
      </Box>
    </Box>
  );
};

export default HTMLViewer;
